/**
 * Diálogo de trazabilidad con timeline visual del ciclo de vida de un prospecto
 */

import { useMemo, useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Chip,
  Paper,
  Stack,
  Avatar,
  Collapse,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Tooltip,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Close,
  Timeline as TimelineIcon,
  AddCircle,
  Edit,
  Delete,
  CheckCircle,
  Cancel,
  Description,
  SwapHoriz,
  PersonAdd,
  Visibility,
  Create,
  ExpandMore,
  ExpandLess,
  ArrowForward,
} from '@mui/icons-material';

import {
  useFetchTrazabilidadProspecto,
  agruparLogsPorEtapa,
  formatFechaAuditoria,
  getAccionColor,
  getEtapaColor,
} from '@/shared/actions/erp/auditoria-ventas-home.actions';
import type { AuditoriaVentasHomeDetalle as AuditoriaDetalle } from '@/shared/interfaces/erp/auditoria-ventas-home.interface';
import {
  ETAPAS_VENTA_ORDEN,
  ETAPA_LABELS,
  type TrazabilidadEtapaGrupo,
} from '@/shared/interfaces/erp/auditoria-ventas-home.interface';

interface Props {
  prospectoId: string;
  prospectoCode?: string;
  open: boolean;
  onClose: () => void;
}

const ACCION_ICONS: Record<string, React.ReactElement> = {
  CREAR: <AddCircle fontSize="small" />,
  ACTUALIZAR: <Edit fontSize="small" />,
  ELIMINAR: <Delete fontSize="small" />,
  APROBAR: <CheckCircle fontSize="small" />,
  RECHAZAR: <Cancel fontSize="small" />,
  ACEPTAR: <CheckCircle fontSize="small" />,
  FIRMAR: <Create fontSize="small" />,
  CARGAR_DOCUMENTO: <Description fontSize="small" />,
  CAMBIAR_ESTADO: <SwapHoriz fontSize="small" />,
  ASIGNAR: <PersonAdd fontSize="small" />,
  REASIGNAR: <PersonAdd fontSize="small" />,
  CANCELAR: <Cancel fontSize="small" />,
  CONSULTAR: <Visibility fontSize="small" />,
};

export const TrazabilidadTimelineDialog = ({ prospectoId, prospectoCode, open, onClose }: Props) => {
  const theme = useTheme();
  const { data: logs, isLoading } = useFetchTrazabilidadProspecto(prospectoId, open);

  const grupos = useMemo(() => {
    if (!logs || !Array.isArray(logs)) return [];
    return agruparLogsPorEtapa(logs);
  }, [logs]);

  const activeStepIndex = useMemo(() => {
    if (!grupos.length) return 0;
    const lastEtapa = grupos[grupos.length - 1].etapa;
    return ETAPAS_VENTA_ORDEN.indexOf(lastEtapa);
  }, [grupos]);

  const completedSteps = useMemo(() => {
    const set = new Set<number>();
    grupos.forEach((g) => {
      const idx = ETAPAS_VENTA_ORDEN.indexOf(g.etapa);
      if (idx >= 0 && idx < activeStepIndex) set.add(idx);
    });
    return set;
  }, [grupos, activeStepIndex]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
          borderBottom: `1px solid ${theme.palette.divider}`,
          pb: 2,
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <TimelineIcon color="primary" />
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Trazabilidad
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {prospectoCode ? `Prospecto: ${prospectoCode}` : prospectoId?.substring(0, 12)}
              {logs && Array.isArray(logs) && ` - ${logs.length} eventos`}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={8}>
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
              Cargando trazabilidad...
            </Typography>
          </Box>
        ) : grupos.length === 0 ? (
          <Box display="flex" justifyContent="center" py={8}>
            <Typography variant="body2" color="text.secondary">
              No se encontraron registros de trazabilidad para este prospecto.
            </Typography>
          </Box>
        ) : (
          <>
            {/* Stepper horizontal de etapas */}
            <Paper
              variant="outlined"
              sx={{ p: 2.5, mb: 3, borderRadius: 2 }}
            >
              <Stepper activeStep={activeStepIndex} alternativeLabel>
                {ETAPAS_VENTA_ORDEN.map((etapa, index) => (
                  <Step key={etapa} completed={completedSteps.has(index)}>
                    <StepLabel
                      StepIconProps={{
                        sx: {
                          color: grupos.some((g) => g.etapa === etapa)
                            ? getEtapaColor(etapa)
                            : undefined,
                          '&.Mui-completed': { color: getEtapaColor(etapa) },
                          '&.Mui-active': { color: getEtapaColor(etapa) },
                        },
                      }}
                    >
                      <Typography variant="caption" fontWeight={600}>
                        {ETAPA_LABELS[etapa]}
                      </Typography>
                      {grupos.find((g) => g.etapa === etapa)?.estado_final && (
                        <Typography variant="caption" display="block" color="text.secondary" fontSize="0.65rem">
                          {grupos.find((g) => g.etapa === etapa)?.estado_final}
                        </Typography>
                      )}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Paper>

            {/* Timeline por etapa */}
            <Stack spacing={3}>
              {grupos.map((grupo) => (
                <EtapaSeccion key={grupo.etapa} grupo={grupo} />
              ))}
            </Stack>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Button onClick={onClose} variant="outlined">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ========================================
// Sub-componentes
// ========================================

const EtapaSeccion = ({ grupo }: { grupo: TrazabilidadEtapaGrupo }) => {
  const etapaColor = getEtapaColor(grupo.etapa);

  return (
    <Box>
      {/* Encabezado de etapa */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          mb: 2,
          pb: 1,
          borderBottom: `2px solid ${alpha(etapaColor, 0.3)}`,
        }}
      >
        <Chip
          label={ETAPA_LABELS[grupo.etapa]}
          sx={{
            bgcolor: etapaColor,
            color: 'white',
            fontWeight: 700,
            fontSize: '0.8rem',
          }}
        />
        <Typography variant="caption" color="text.secondary">
          {grupo.eventos.length} evento{grupo.eventos.length !== 1 ? 's' : ''}
        </Typography>
        {grupo.estado_final && (
          <Chip
            label={grupo.estado_final}
            size="small"
            variant="outlined"
            sx={{ ml: 'auto', fontWeight: 600, fontSize: '0.7rem' }}
          />
        )}
        {grupo.fecha_inicio && (
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.68rem' }}>
            {formatFechaAuditoria(grupo.fecha_inicio)}
          </Typography>
        )}
      </Box>

      {/* Timeline vertical */}
      <Box
        sx={{
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 15,
            width: 2,
            bgcolor: alpha(etapaColor, 0.25),
          },
        }}
      >
        <Stack spacing={1.5} sx={{ pl: 5 }}>
          {grupo.eventos.map((evento) => (
            <TimelineItem key={evento.id_bitacora} evento={evento} etapaColor={etapaColor} />
          ))}
        </Stack>
      </Box>
    </Box>
  );
};

const TimelineItem = ({ evento, etapaColor }: { evento: AuditoriaDetalle; etapaColor: string }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);

  const cambiosEntries = useMemo(() => {
    if (!evento.cambios) return [];
    return Object.entries(evento.cambios).filter(
      ([key]) => !key.endsWith('_data') && key !== 'id' && !key.startsWith('id_')
    );
  }, [evento.cambios]);

  const hasCambios = cambiosEntries.length > 0;

  // Detectar cambio de estado
  const estadoCambio = useMemo(() => {
    if (!evento.cambios) return null;
    const estadoKeys = ['estado_prospecto', 'estado_preventa', 'estado_preagendamiento', 'estado_agendamiento', 'estado'];
    for (const key of estadoKeys) {
      if (evento.cambios[key]) {
        return { campo: key, anterior: evento.cambios[key].anterior, nuevo: evento.cambios[key].nuevo };
      }
    }
    return null;
  }, [evento.cambios]);

  const accionIcon = ACCION_ICONS[evento.accion] || <Edit fontSize="small" />;

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Dot en la línea */}
      <Avatar
        sx={{
          position: 'absolute',
          left: -41,
          top: 8,
          width: 28,
          height: 28,
          bgcolor: alpha(etapaColor, 0.15),
          color: etapaColor,
          fontSize: '0.85rem',
        }}
      >
        {accionIcon}
      </Avatar>

      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          cursor: hasCambios ? 'pointer' : 'default',
          transition: 'all 0.15s ease',
          '&:hover': hasCambios
            ? { bgcolor: alpha(theme.palette.primary.main, 0.03), borderColor: alpha(etapaColor, 0.4) }
            : undefined,
        }}
        onClick={() => hasCambios && setExpanded(!expanded)}
      >
        {/* Fila superior: acción + usuario + fecha */}
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap">
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Chip
              label={evento.accion}
              size="small"
              color={getAccionColor(evento.accion)}
              sx={{ fontWeight: 600, fontSize: '0.68rem', height: 22 }}
            />
            <Typography variant="caption" fontWeight={500}>
              {evento.usuario_nombre || 'Sistema'}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.68rem' }}>
              {evento.fecha_hora_local || formatFechaAuditoria(evento.fecha_hora)}
            </Typography>
            {hasCambios && (
              <Tooltip title={expanded ? 'Ocultar cambios' : 'Ver cambios'}>
                <IconButton size="small" sx={{ p: 0.25 }}>
                  {expanded ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        {/* Transición de estado */}
        {estadoCambio && (
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Chip
              label={String(estadoCambio.anterior || '—')}
              size="small"
              variant="outlined"
              color="default"
              sx={{ fontSize: '0.65rem', height: 20 }}
            />
            <ArrowForward sx={{ fontSize: 14, color: 'text.disabled' }} />
            <Chip
              label={String(estadoCambio.nuevo)}
              size="small"
              color="primary"
              sx={{ fontSize: '0.65rem', height: 20, fontWeight: 600 }}
            />
          </Box>
        )}

        {/* Descripción */}
        {evento.descripcion && !estadoCambio && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.78rem' }}>
            {evento.descripcion}
          </Typography>
        )}

        {/* Cambios expandibles */}
        <Collapse in={expanded}>
          <Box
            sx={{
              mt: 1.5,
              pt: 1.5,
              borderTop: `1px solid ${theme.palette.divider}`,
              maxHeight: 300,
              overflow: 'auto',
            }}
          >
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Cambios realizados ({cambiosEntries.length})
            </Typography>
            <Stack spacing={0.75}>
              {cambiosEntries.map(([campo, cambio]) => (
                <Box
                  key={campo}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    fontSize: '0.75rem',
                    py: 0.5,
                    px: 1,
                    borderRadius: 1,
                    bgcolor: alpha(theme.palette.grey[500], 0.04),
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    sx={{ minWidth: 120, color: 'text.secondary', fontFamily: 'monospace' }}
                  >
                    {campo}
                  </Typography>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'error.main',
                        textDecoration: 'line-through',
                        display: 'block',
                        wordBreak: 'break-all',
                      }}
                    >
                      {formatCambioValue((cambio as any)?.anterior)}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: 'success.main', fontWeight: 500, display: 'block', wordBreak: 'break-all' }}
                    >
                      {formatCambioValue((cambio as any)?.nuevo)}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Stack>
          </Box>
        </Collapse>
      </Paper>
    </Box>
  );
};

const formatCambioValue = (value: any): string => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
  return String(value);
};
